// WorkItemActionBar (M1 §8, REQ-WF-006): `allowedActions` ∩ `usePermission(item.scope)`. O escopo do item pode
// não ser o contexto ativo da rota (a inbox agrega Rede + N tenants), por isso a checagem usa `effectivePermissions`
// diretamente sobre o `MeContext` do item, em vez do hook ligado à rota (`usePermission`).
import { Button } from '@giyus/ui';
import { useTranslation } from 'react-i18next';

import { useMe } from '../../../lib/auth/authContext';
import { effectivePermissions } from '../../../lib/permissions/usePermission';
import type { WorkflowScopeRef } from '../api/contract.preview';

type WorkflowAction =
  'claim' | 'start' | 'complete' | 'block' | 'cancel' | 'assign' | 'decide' | 'resolve' | 'dismiss';

/** Ações base (implícitas — Anexo A.1 §6.1: `task.work`/`inbox.read`) × ações que exigem uma permissão N/T/S. */
const REQUIRED_PERMISSION: Partial<Record<WorkflowAction, string>> = {
  cancel: 'task.manage',
  assign: 'task.manage',
  decide: 'approval.decide',
  resolve: 'exception.manage',
  dismiss: 'exception.manage',
};

export interface WorkItemActionBarProps {
  readonly scope: WorkflowScopeRef;
  readonly allowedActions: readonly string[];
  readonly onAction: (action: WorkflowAction) => void;
  readonly pending?: WorkflowAction | null;
}

export function WorkItemActionBar({
  scope,
  allowedActions,
  onAction,
  pending,
}: WorkItemActionBarProps) {
  const { t } = useTranslation('workflow');
  const me = useMe();
  const context =
    scope.level === 'Network'
      ? null
      : (me.contexts.find((c) => c.tenantId === scope.tenantId) ?? null);
  const permissions = effectivePermissions({
    me,
    scope: scope.level === 'Network' ? 'network' : 'tenant',
    context,
  });

  const visible = allowedActions.filter((action): action is WorkflowAction => {
    const required = REQUIRED_PERMISSION[action as WorkflowAction];
    return required === undefined || permissions.has(required);
  });

  if (visible.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap gap-2" data-testid="work-item-actions">
      {visible.map((action) => (
        <Button
          key={action}
          size="sm"
          variant={action === 'cancel' || action === 'dismiss' ? 'outline' : 'default'}
          disabled={pending === action}
          onClick={() => {
            onAction(action);
          }}
          data-testid={`work-item-action-${action}`}
        >
          {t(`actions.${action}`)}
        </Button>
      ))}
    </div>
  );
}
